import { useMemo, useState } from "react";
import {
  AlertTriangle,
  LayoutDashboard,
  Loader2,
  RefreshCw,
  RotateCcw,
  Save,
  Settings,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  usePersonalDashboard,
  usePlatformDashboard,
  useRefreshDashboard,
} from "../hooks/useDashboard";
import { useWidgetLayout } from "../hooks/useWidgetLayout";
import { WidgetWrapper } from "../components/WidgetWrapper";
import { QuickStatsWidget } from "../widgets/QuickStatsWidget";
import { MyTasksWidget } from "../widgets/MyTasksWidget";
import { MyActionsWidget } from "../widgets/MyActionsWidget";
import { UpcomingMeetingsWidget } from "../widgets/UpcomingMeetingsWidget";
import { OverdueItemsWidget } from "../widgets/OverdueItemsWidget";
import { RecentNotesWidget } from "../widgets/RecentNotesWidget";
import { PlatformCountsWidget } from "../widgets/PlatformCountsWidget";
import { ProjectHealthWidget } from "../widgets/ProjectHealthWidget";
import { RecentActivityWidget } from "../widgets/RecentActivityWidget";
import { UpcomingMilestonesWidget } from "../widgets/UpcomingMilestonesWidget";
import { PendingExpensesWidget } from "../widgets/PendingExpensesWidget";
import { BudgetSummaryWidget } from "../widgets/BudgetSummaryWidget";
import { ExpiringCredentialsWidget } from "../widgets/ExpiringCredentialsWidget";
import { IdeasLeaderboardWidget } from "../widgets/IdeasLeaderboardWidget";
import { Empty, timeAgo } from "../widgets/_shared";
import type { PersonalDashboard, PlatformDashboard, WidgetConfig } from "../types";

function greeting(): string {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  if (h >= 17 && h < 21) return "Good evening";
  return "Good night";
}

function formatDateLong(d: Date): string {
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function firstName(name: string): string {
  return name.split(" ")[0] ?? name;
}

function summarySentence(p: PersonalDashboard | undefined): string {
  if (!p) return "Loading your activity…";
  const s = p.myStats;
  if (s.overdueItems > 0) {
    return `You have ${s.overdueItems} overdue item${
      s.overdueItems === 1 ? "" : "s"
    } requiring attention`;
  }
  if (s.meetingsToday > 0) {
    return `You have ${s.meetingsToday} meeting${
      s.meetingsToday === 1 ? "" : "s"
    } today`;
  }
  if (s.openTasks > 0) {
    return `You have ${s.openTasks} open task${
      s.openTasks === 1 ? "" : "s"
    }`;
  }
  return "Everything looks good today! ✅";
}

export default function DashboardPage() {
  const { user } = useAuth();
  const personalQuery = usePersonalDashboard();
  const platformQuery = usePlatformDashboard();
  const refresh = useRefreshDashboard();

  const userId = user?.id ?? "anon";
  const {
    personalWidgets,
    platformWidgets,
    isEditMode,
    setIsEditMode,
    saveLayout,
    resetLayout,
    toggleWidget,
    moveWidget,
  } = useWidgetLayout(userId);

  const [refreshing, setRefreshing] = useState(false);

  // Permission-filter widgets: a widget is shown only if the user can view at
  // least one of its mapped modules (Super Admin always; no moduleKeys = always
  // shown). A role with no widget-eligible access (e.g. a QA-only tester) gets
  // an empty state per zone.
  const isSuperAdmin = !!user?.roles.some((r) => r.name === "Super Admin");
  const canSeeWidget = useMemo(() => {
    const perms = user?.permissions;
    return (w: WidgetConfig): boolean => {
      if (!w.moduleKeys || w.moduleKeys.length === 0) return true;
      if (isSuperAdmin) return true;
      return w.moduleKeys.some((k) => !!perms?.[k]?.canView);
    };
  }, [user, isSuperAdmin]);

  const visiblePersonalWidgets = useMemo(
    () => personalWidgets.filter(canSeeWidget),
    [personalWidgets, canSeeWidget],
  );
  const visiblePlatformWidgets = useMemo(
    () => platformWidgets.filter(canSeeWidget),
    [platformWidgets, canSeeWidget],
  );

  function handleRefresh() {
    refresh();
    setRefreshing(true);
    window.setTimeout(() => setRefreshing(false), 1000);
  }

  const lastUpdated = personalQuery.dataUpdatedAt
    ? new Date(personalQuery.dataUpdatedAt).toISOString()
    : null;

  const personalError = personalQuery.isError;
  const platformError = platformQuery.isError;

  return (
    <AppLayout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {greeting()}, {firstName(user?.name ?? "there")} 👋
            </h1>
            <div className="mt-1 text-[13px] text-muted-foreground">
              {formatDateLong(new Date())} · {summarySentence(personalQuery.data)}
            </div>
            <div className="mt-1 inline-flex items-center gap-2 text-[11px] text-muted-foreground">
              {lastUpdated && <span>Updated {timeAgo(lastUpdated)}</span>}
              <button
                type="button"
                onClick={handleRefresh}
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-[hsl(210,50%,96%)]"
                title="Refresh"
              >
                <RefreshCw
                  className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`}
                  strokeWidth={1.5}
                />
                Refresh
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEditMode ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditMode(true)}
              >
                <Settings className="h-3.5 w-3.5" strokeWidth={1.5} />
                Customize
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={saveLayout}>
                  <Save className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Save Layout
                </Button>
                <Button size="sm" variant="outline" onClick={resetLayout}>
                  <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Reset
                </Button>
              </>
            )}
          </div>
        </div>

        {isEditMode && (
          <div
            className="flex items-center gap-2 rounded-md border px-3 py-2 text-[12px]"
            style={{
              background: "hsl(45,90%,95%)",
              borderColor: "hsl(45,90%,80%)",
              color: "hsl(35,90%,30%)",
            }}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
            Dashboard edit mode — show/hide widgets and reorder them.
          </div>
        )}

        {/* ZONE 1 — MY WORK */}
        <section>
          <SectionLabel label="MY WORK" />
          {personalError ? (
            <ErrorCard
              message="Failed to load your dashboard data."
              onRetry={() => personalQuery.refetch()}
            />
          ) : (
            <PersonalZone
              data={personalQuery.data}
              isLoading={personalQuery.isLoading}
              widgets={visiblePersonalWidgets}
              isEditMode={isEditMode}
              onMove={(id, dir) => moveWidget("personal", id, dir)}
              onToggle={(id) => toggleWidget("personal", id)}
            />
          )}
        </section>

        {/* ZONE 2 — PLATFORM OVERVIEW */}
        <section>
          <div
            className="my-5 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />
          <SectionLabel label="PLATFORM OVERVIEW" />
          {platformError ? (
            <ErrorCard
              message="Failed to load platform stats."
              onRetry={() => platformQuery.refetch()}
            />
          ) : (
            <PlatformZone
              data={platformQuery.data}
              isLoading={platformQuery.isLoading}
              widgets={visiblePlatformWidgets}
              isEditMode={isEditMode}
              onMove={(id, dir) => moveWidget("platform", id, dir)}
              onToggle={(id) => toggleWidget("platform", id)}
            />
          )}
        </section>
      </div>
    </AppLayout>
  );
}

function WidgetsEmptyState() {
  return (
    <Empty
      icon={<LayoutDashboard className="h-6 w-6" strokeWidth={1.5} />}
      text="No dashboard widgets for your current access"
    />
  );
}

function SectionLabel({ label }: { label: string }) {
  return (
    <div className="mb-3 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      className="flex items-center justify-between rounded-xl border px-4 py-3 text-[13px]"
      style={{
        background: "hsl(0,70%,97%)",
        borderColor: "hsl(0,70%,85%)",
        color: "hsl(0,70%,40%)",
      }}
    >
      <span>{message}</span>
      <Button size="sm" variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

function LoadingWidget({ height = 160 }: { height?: number }) {
  return (
    <div
      className="animate-pulse rounded-xl"
      style={{
        height,
        background: "hsl(210,16%,95%)",
        border: "1px solid hsl(210,30%,90%)",
      }}
    />
  );
}

function sortedByOrder<T extends WidgetConfig>(items: T[]): T[] {
  return [...items].sort((a, b) => a.order - b.order);
}

/* ──────────────────────────── Personal zone ─────────────────────────── */

function PersonalZone({
  data,
  isLoading,
  widgets,
  isEditMode,
  onMove,
  onToggle,
}: {
  data: PersonalDashboard | undefined;
  isLoading: boolean;
  widgets: WidgetConfig[];
  isEditMode: boolean;
  onMove: (id: string, direction: "up" | "down") => void;
  onToggle: (id: string) => void;
}) {
  const sorted = useMemo(() => sortedByOrder(widgets), [widgets]);
  const full = sorted.filter((w) => w.size === "full");
  const half = sorted.filter((w) => w.size === "half");

  if (sorted.length === 0) return <WidgetsEmptyState />;

  return (
    <div className="space-y-4">
      {full.map((w, i, arr) => (
        <WidgetWrapper
          key={w.id}
          id={w.id}
          title={widgetTitle(w.id)}
          visible={w.visible}
          isEditMode={isEditMode}
          order={w.order}
          onMoveUp={() => onMove(w.id, "up")}
          onMoveDown={() => onMove(w.id, "down")}
          onToggle={() => onToggle(w.id)}
          canMoveUp={i > 0}
          canMoveDown={i < arr.length - 1}
        >
          {renderPersonalWidget(w.id, data, isLoading)}
        </WidgetWrapper>
      ))}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}
      >
        {half.map((w, i, arr) => (
          <WidgetWrapper
            key={w.id}
            id={w.id}
            title={widgetTitle(w.id)}
            visible={w.visible}
            isEditMode={isEditMode}
            order={w.order}
            onMoveUp={() => onMove(w.id, "up")}
            onMoveDown={() => onMove(w.id, "down")}
            onToggle={() => onToggle(w.id)}
            canMoveUp={i > 0}
            canMoveDown={i < arr.length - 1}
          >
            {renderPersonalWidget(w.id, data, isLoading)}
          </WidgetWrapper>
        ))}
      </div>
    </div>
  );
}

function renderPersonalWidget(
  id: string,
  data: PersonalDashboard | undefined,
  isLoading: boolean,
): React.ReactElement {
  switch (id) {
    case "quick-stats":
      if (!data) return <LoadingWidget height={96} />;
      return <QuickStatsWidget stats={data.myStats} />;
    case "my-tasks":
      return (
        <MyTasksWidget
          tasks={data?.myTasks ?? []}
          isLoading={isLoading}
        />
      );
    case "my-actions":
      return (
        <MyActionsWidget
          actions={data?.myActions ?? []}
          isLoading={isLoading}
        />
      );
    case "upcoming-meetings":
      return (
        <UpcomingMeetingsWidget
          meetings={data?.upcomingMeetings ?? []}
          isLoading={isLoading}
        />
      );
    case "overdue-items":
      return (
        <OverdueItemsWidget
          items={data?.overdueItems ?? []}
          isLoading={isLoading}
        />
      );
    case "recent-notes":
      return (
        <RecentNotesWidget
          notes={data?.recentNotes ?? []}
          isLoading={isLoading}
        />
      );
    case "my-ideas":
      return (
        <IdeasLeaderboardWidget ideas={[]} isLoading={isLoading} />
      );
    default:
      return <LoadingWidget />;
  }
}

function widgetTitle(id: string): string {
  const map: Record<string, string> = {
    "quick-stats": "Quick Stats",
    "my-tasks": "My Tasks",
    "my-actions": "My Actions",
    "upcoming-meetings": "Upcoming Meetings",
    "overdue-items": "Overdue Items",
    "recent-notes": "Recent Notes",
    "my-ideas": "My Ideas",
    "platform-counts": "Platform Counts",
    "project-health": "Project Health",
    "recent-activity": "Recent Activity",
    "upcoming-milestones": "Upcoming Milestones",
    "pending-expenses": "Pending Expenses",
    "budget-summary": "Budget Summary",
    "expiring-credentials": "Expiring Credentials",
    "ideas-leaderboard": "Top Ideas",
  };
  return map[id] ?? id;
}

/* ──────────────────────────── Platform zone ─────────────────────────── */

function PlatformZone({
  data,
  isLoading,
  widgets,
  isEditMode,
  onMove,
  onToggle,
}: {
  data: PlatformDashboard | undefined;
  isLoading: boolean;
  widgets: WidgetConfig[];
  isEditMode: boolean;
  onMove: (id: string, direction: "up" | "down") => void;
  onToggle: (id: string) => void;
}) {
  const sorted = useMemo(() => sortedByOrder(widgets), [widgets]);
  const full = sorted.filter((w) => w.size === "full");
  const half = sorted.filter((w) => w.size === "half");

  if (sorted.length === 0) return <WidgetsEmptyState />;

  return (
    <div className="space-y-4">
      {isLoading && !data && (
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
          Loading platform stats…
        </div>
      )}
      {full.map((w, i, arr) => (
        <WidgetWrapper
          key={w.id}
          id={w.id}
          title={widgetTitle(w.id)}
          visible={w.visible}
          isEditMode={isEditMode}
          order={w.order}
          onMoveUp={() => onMove(w.id, "up")}
          onMoveDown={() => onMove(w.id, "down")}
          onToggle={() => onToggle(w.id)}
          canMoveUp={i > 0}
          canMoveDown={i < arr.length - 1}
        >
          {renderPlatformWidget(w.id, data, isLoading)}
        </WidgetWrapper>
      ))}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}
      >
        {half.map((w, i, arr) => (
          <WidgetWrapper
            key={w.id}
            id={w.id}
            title={widgetTitle(w.id)}
            visible={w.visible}
            isEditMode={isEditMode}
            order={w.order}
            onMoveUp={() => onMove(w.id, "up")}
            onMoveDown={() => onMove(w.id, "down")}
            onToggle={() => onToggle(w.id)}
            canMoveUp={i > 0}
            canMoveDown={i < arr.length - 1}
          >
            {renderPlatformWidget(w.id, data, isLoading)}
          </WidgetWrapper>
        ))}
      </div>
    </div>
  );
}

function renderPlatformWidget(
  id: string,
  data: PlatformDashboard | undefined,
  isLoading: boolean,
): React.ReactElement {
  switch (id) {
    case "platform-counts":
      if (!data) return <LoadingWidget height={180} />;
      return <PlatformCountsWidget counts={data.counts} />;
    case "project-health":
      return (
        <ProjectHealthWidget
          projects={data?.projectHealth ?? []}
          isLoading={isLoading}
        />
      );
    case "recent-activity":
      return (
        <RecentActivityWidget
          activities={data?.recentActivity ?? []}
          isLoading={isLoading}
        />
      );
    case "upcoming-milestones":
      return (
        <UpcomingMilestonesWidget
          milestones={data?.upcomingMilestones ?? []}
          isLoading={isLoading}
        />
      );
    case "pending-expenses":
      return (
        <PendingExpensesWidget
          expenses={data?.pendingExpenses ?? []}
          isLoading={isLoading}
        />
      );
    case "budget-summary":
      if (!data) return <LoadingWidget />;
      return <BudgetSummaryWidget summary={data.budgetSummary} />;
    case "expiring-credentials":
      return (
        <ExpiringCredentialsWidget
          credentials={data?.expiringCredentials ?? []}
          isLoading={isLoading}
        />
      );
    case "ideas-leaderboard":
      return (
        <IdeasLeaderboardWidget
          ideas={data?.topIdeas ?? []}
          isLoading={isLoading}
        />
      );
    default:
      return <LoadingWidget />;
  }
}
